import { useEffect, useMemo, useState, createElement } from 'react';
import { TechnologyDetails } from '@vfit/shared/atoms';
import { ModemDetailsV2 as ModemDetails } from '@vfit/shared/components';
import { API, getFromLocalStorageByKey, TechnologyCategories } from '@vfit/shared/data-access';
import {
  ISlides,
  useCmsConfig,
  ITechCard,
  ITechs,
  IDevices,
  IShoppingCartResponse,
  retrieveProduct,
  retrieveProductTechnology,
  recoverServiceabilityField,
  CONSUMER_CMS_DEFAULT,
  IRemappingTechnology,
} from '@vfit/consumer/data-access';
import { removeCurrency, useTechnologyCardsFlow } from '@vfit/consumer/hooks';
import { SoftManagerService } from '@vfit/consumer/providers';
import { useTracking } from '@vfit/shared/data-access';
import { useQueryClient } from 'react-query';
import { IOfferingServiceabilityResponse } from '@vfit/shared/models';
import { ITechCardInfo, ITechModal } from './technologyCards.models';
import {
  organizeTechnologyCardModal,
  organizeTechCardsProductDetail,
} from './technologyCards.utils';

export const useTechnologyCards = () => {
  const productTracking = retrieveProduct();
  const shoppingCart: IShoppingCartResponse = getFromLocalStorageByKey('shoppingCart');
  const client = useQueryClient();
  const newRemappingTech = (client?.getQueryData('getCmsTecnologies') || {})?.[
    'remappingtechnology'
  ] as IRemappingTechnology;

  const trackingProduct = useMemo(
    () => ({
      cart_id: (shoppingCart as IShoppingCartResponse)?.id,
      cart_product_id: `${productTracking?.offerId}`,
      cart_product_name: productTracking?.slug,
      cart_product_category: 'fixed',
      cart_product_price: productTracking?.price && removeCurrency(productTracking.price),
      cart_product_quantity: '1',
    }),
    [shoppingCart, productTracking?.offerId, productTracking?.slug, productTracking?.price],
  );

  const opts = useMemo(
    () => ({
      event_category: 'sales',
      page_subsection: 'checkout',
      page_section: 'fixed offers',
      page_type: 'cart',
      journey_name: 'checkout',
      product_technology: retrieveProductTechnology(),
    }),
    [],
  );

  const { trackView } = useTracking({
    event: ['cart_view'],
    opts,
    cartProduct: trackingProduct,
    visitorTrackingOpts: SoftManagerService(client).getUser(),
    disableInitialTrack: true,
  });

  const { serviceability, tecnology, isSuccess } = useTechnologyCardsFlow(newRemappingTech);
  const [techCard, setTechCard] = useState<ISlides[]>([]);
  const [cmsTechConfig, setCmsTechConfig] = useState<ITechCard | undefined>();
  const [techCardInfo, setTechCardInfo] = useState<ITechCardInfo>({
    download: 'NA',
    upload: 'NA',
    latency: 'NA',
  });
  const allTechs = useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_TECNOLOGIES) as ITechs;
  const allDevices = useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_DEVICES) as IDevices;

  const DEFAULT_MODAL: ITechModal = {
    show: false,
    component: undefined,
  };

  const convertSpeed = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    let mbit;

    if (num % 1024 === 0) {
      mbit = num / 1024;
    }

    if (num % 1000 === 0) {
      mbit = num / 1000;
    }

    if (mbit >= 1000) {
      const gbit = mbit / 1000;
      return Number.isInteger(gbit) ? `${gbit} Gbit/s*` : `${gbit?.toFixed(1)} Gbit/s*`;
    }

    return Number.isInteger(mbit) ? `${mbit} Mbit/s*` : `${mbit?.toFixed(1)} Mbit/s*`;
  };

  const convertDownload = (value: string) => {
    const velocity = parseFloat(value);
    if (isNaN(velocity)) return value;
    let num;

    if (velocity % 1024 === 0) {
      num = velocity / 1024;
    } else if (velocity % 1000 === 0) {
      num = velocity / 1000;
    } else {
      num = velocity;
    }

    if (num / 1000000 > 1) {
      const gbit = num / 1000000;
      return Number.isInteger(gbit) ? `${gbit} Gbit/s*` : `${gbit?.toFixed(1)} Gbit/s*`;
    } else if (num / 100000 > 1) {
      const gbit = num / 1000;
      return Number.isInteger(gbit) ? `${gbit} Mbit/s*` : `${gbit?.toFixed(1)} Mbit/s*`;
    } else if (num >= 1000) {
      const gbit = num / 1000;
      return Number.isInteger(gbit) ? `${gbit} Gbit/s*` : `${gbit?.toFixed(1)} Gbit/s*`;
    }

    return Number.isInteger(num) ? `${num} Mbit/s*` : `${num?.toFixed(1)} Mbit/s*`;
  };

  useEffect(() => {
    if (serviceability) {
      const download = convertDownload(
        recoverServiceabilityField(
          (serviceability as IOfferingServiceabilityResponse)?.serviceabilityCharacteristic,
          'MaxDownloadSpeed',
          true,
        ),
      );
      const upload = convertSpeed(
        recoverServiceabilityField(
          (serviceability as IOfferingServiceabilityResponse)?.serviceabilityCharacteristic,
          'MaxUploadSpeed',
        ),
      );
      const latency = recoverServiceabilityField(
        (serviceability as IOfferingServiceabilityResponse)?.serviceabilityCharacteristic,
        'Latency',
      )
        ? `${recoverServiceabilityField(
            (serviceability as IOfferingServiceabilityResponse)?.serviceabilityCharacteristic,
            'Latency',
          )} ms`
        : '-';
      setTechCardInfo({
        download,
        upload,
        latency,
      });
    }
  }, [isSuccess, serviceability, tecnology?.mappedCategory]);

  let tech: ITechCard | undefined;
  const switchTech = () => {
    if (allTechs?.[tecnology?.originalTechType || '']?.technology?.detail?.title) {
      tech = allTechs?.[tecnology?.originalTechType || ''];
    }
    switch (tecnology?.mappedCategory) {
      case TechnologyCategories.FTTH:
        setCmsTechConfig(allTechs.ftth);
        tech = allTechs.ftth;
        break;
      case TechnologyCategories.FTTC:
        setCmsTechConfig(allTechs.fttc);
        tech = allTechs.fttc;
        break;
      case TechnologyCategories.ADSL:
        setCmsTechConfig(allTechs.adsl);
        tech = allTechs.adsl;
        break;
      case TechnologyCategories.FWAINDOOR:
        setCmsTechConfig(allTechs.FWAINDOOR);
        tech = allTechs.FWAINDOOR;
        break;
      case TechnologyCategories.FWAOUTDOOR:
        setCmsTechConfig(allTechs.FWAOUTDOOR);
        tech = allTechs.FWAOUTDOOR;
        break;
      case TechnologyCategories.FWAOUTDOOR5G:
        setCmsTechConfig(allTechs.FWAOUTDOOR5G);
        tech = allTechs.FWAOUTDOOR5G;
        break;
      case TechnologyCategories.FTTHTIM:
        setCmsTechConfig(allTechs.FTTHTIM);
        tech = allTechs.FTTHTIM;
        break;
      case TechnologyCategories.FTTHCD:
        setCmsTechConfig(allTechs.FTTHCD);
        tech = allTechs.FTTHCD;
        break;
      case TechnologyCategories.FWAINDOOR5G:
        setCmsTechConfig(allTechs.FWAINDOOR5G);
        tech = allTechs.FWAINDOOR5G;
        break;
      default:
        break;
    }
    if (tecnology?.mappedCategory) {
      const organizedCards = organizeTechCardsProductDetail(tech, tecnology?.mappedCategory);

      setTechCard(organizedCards);
    }
  };

  useEffect(() => {
    if (tecnology) switchTech();
  }, [tecnology]);

  const [techModal, setTechModal] = useState<ITechModal>(DEFAULT_MODAL);

  const handleCloseModal = () => setTechModal(DEFAULT_MODAL);

  const showDetail = (product: ISlides) => {
    if (!product.showInfoLine) {
      trackView({
        event_name: 'ui_interaction',
        event_label_track: `${product.detail?.title
          ?.replace(/(<([^>]+)>)/gi, '')
          .replace(/(?:\r\n|\r|\n)/g, '')}`,
        event_category: 'users',
        event_action: 'click',
      });
      setTechModal({
        show: true,
        tabletMode: false,
        component: createElement(ModemDetails, {
          title: product?.detail?.title || '',
          content: product?.detail?.description?.textSimple || '',
        }),
      });
    } else {
      const {
        title: techTitle,
        firstDescription,
        secondDescription,
        imageSrc,
        subtitle,
        accordions,
      } = organizeTechnologyCardModal(cmsTechConfig as ITechCard);
      trackView({
        event_name: 'ui_interaction',
        event_label_track: 'technology details',
        event_category: 'users',
        event_action: 'click',
      });
      setTechModal({
        show: true,
        tabletMode: true,
        containerHeight: '80%',
        component: createElement(TechnologyDetails, {
          title: techTitle,
          firstDescription,
          accordions,
          secondDescription,
          imageSrc,
          subtitle,
        }),
      });
    }
  };

  const info = {
    download:
      cmsTechConfig?.technology?.info?.download !== undefined &&
      cmsTechConfig?.technology?.info?.download !== ''
        ? `${cmsTechConfig?.technology?.info?.download?.replace('$DOWNLOAD', techCardInfo?.download)}`
        : `Fino a: ${techCardInfo?.download}`,
    upload:
      cmsTechConfig?.technology?.info?.upload !== undefined &&
      cmsTechConfig?.technology?.info?.upload !== ''
        ? `${cmsTechConfig?.technology?.info?.upload?.replace('$UPLOAD', techCardInfo?.upload)}`
        : `Fino a: ${techCardInfo?.upload}`,
    latency:
      cmsTechConfig?.technology?.info?.latency !== undefined &&
      cmsTechConfig?.technology?.info?.latency !== ''
        ? `${cmsTechConfig?.technology?.info?.latency?.replace('$LATENCY', techCardInfo?.latency)}`
        : `${techCardInfo.latency}`,
  };

  return {
    techCard,
    info,
    techModal,
    handleCloseModal,
    showDetail,
  };
};

